/**
 * Admin login. Cognito's email OTP flow, and nothing else.
 *
 * The legacy fallback that used to sit here is gone. It never authenticated
 * anything: it checked the passphrase field was non-empty, discarded it, and
 * called setSession() with whichever name was picked from a dropdown — so any
 * string signed you in as anyone, entirely client-side. It only rendered when
 * /api/auth/config reported auth off, which no deployed tier does, but a login
 * form that performs no check is worse to leave lying around than to delete.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  answerEmailOtp,
  displayNameFromIdToken,
  fetchAuthConfig,
  startEmailOtp,
  type AuthConfig,
} from '../auth.js';
import { setCognitoSession } from '../session.js';
import { BrandMark } from '../components/BrandMark.js';
import {
  recordSuccessfulSessionWrite,
  reportSignInSuccess,
  userSubFromIdToken,
} from '../lib/auth-diag.js';

export function LoginScreen() {
  const navigate = useNavigate();
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [email, setEmail] = useState('clint@liber8.consulting');
  const [code, setCode] = useState('');
  const [cognitoSession, setCognitoSessionValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchAuthConfig()
      .then(setAuthConfig)
      .catch(() => setAuthConfig({ authEnabled: false }));
  }, []);

  async function onRequestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!authConfig?.authEnabled) return;
    setBusy(true);
    setError(null);
    try {
      const res = await startEmailOtp(authConfig, email);
      if (res.AuthenticationResult?.IdToken) {
        finishCognitoLogin(res.AuthenticationResult.IdToken, res.AuthenticationResult);
        return;
      }
      if (!res.Session) throw new Error('Cognito did not return a sign-in session.');
      setCognitoSessionValue(res.Session);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send sign-in code.');
    } finally {
      setBusy(false);
    }
  }

  async function onVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (!authConfig?.authEnabled || !cognitoSession) return;
    setBusy(true);
    setError(null);
    try {
      const res = await answerEmailOtp({
        config: authConfig,
        email,
        code,
        session: cognitoSession,
      });
      if (!res.AuthenticationResult?.IdToken) {
        throw new Error('Cognito did not return tokens for this code.');
      }
      finishCognitoLogin(res.AuthenticationResult.IdToken, res.AuthenticationResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code verification failed.');
    } finally {
      setBusy(false);
    }
  }

  function finishCognitoLogin(
    idToken: string,
    result: {
      AccessToken?: string | undefined;
      RefreshToken?: string | undefined;
      ExpiresIn?: number | undefined;
    },
  ) {
    const session = setCognitoSession({
      name: displayNameFromIdToken(idToken, email),
      email,
      idToken,
      ...(result.AccessToken !== undefined ? { accessToken: result.AccessToken } : {}),
      ...(result.RefreshToken !== undefined ? { refreshToken: result.RefreshToken } : {}),
      ...(result.ExpiresIn !== undefined ? { expiresInSeconds: result.ExpiresIn } : {}),
    });
    const userSub = userSubFromIdToken(idToken);
    recordSuccessfulSessionWrite({ deviceId: session.deviceId, userSub });
    reportSignInSuccess({ userSub });
    void navigate('/', { replace: true });
  }

  return (
    <div className="screen screen--center">
      <div className="screen screen--narrow">
        <div className="login-card">
          <BrandMark />
          <p className="subtitle">Sign in with your email code.</p>
          {authConfig?.authEnabled ? (
            <form
              onSubmit={(e) => {
                void (cognitoSession ? onVerifyCode(e) : onRequestCode(e));
              }}
            >
              <label>
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={Boolean(cognitoSession)}
                />
              </label>
              {cognitoSession && (
                <label>
                  <span>One-time code</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </label>
              )}
              {error && <p className="error">{error}</p>}
              <button type="submit" className="primary" disabled={busy}>
                {cognitoSession ? 'Verify code' : 'Email me a code'}
              </button>
              {cognitoSession && (
                <button
                  type="button"
                  onClick={() => {
                    setCognitoSessionValue('');
                    setCode('');
                  }}
                  disabled={busy}
                >
                  Use a different email
                </button>
              )}
            </form>
          ) : (
            // No Cognito config means no way in. Say so plainly rather than
            // offering a form that authenticates nobody.
            <p className="error">
              Sign-in is unavailable: this deployment has no Cognito configuration.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
